import { Injectable } from '@angular/core';
import { TranslationConfigService } from '@services';
import { ClientService, ResponseType } from '@vi-iot/client';
import {
  AssistantApiModel,
  AssistantFeedbackParams,
  AssistantDocumentDetails,
  AssistantFeedbackData,
  AssistantFeedbackDataParams,
  AssistantFilterParams,
  AssistantFiltersData,
  AssistantSearchData,
  AssistantSearchParams,
  AssistantSuggestData,
} from '@vi-iot/client/apis/assistant/assistant.api-model';
import moment from 'moment/moment';
import { Observable } from 'rxjs';
import {
  AppServiceType,
  AssistantFeedbackSortOrder,
  AssistantFilterPayload,
  AssistantSupportedLanguages,
  assistantSupportedLanguagesList,
} from '../../../../app/@store/assistant';

@Injectable({ providedIn: 'root' })
export class AssistantApi implements AssistantApiModel {
  private readonly baseUrl = 'empolis/v1';
  defaultLanguage = '';

  constructor(
    private clientService: ClientService,
    private translationConfigService: TranslationConfigService
  ) {
    const language = this.translationConfigService.getLanguage();
    this.defaultLanguage = assistantSupportedLanguagesList.includes(language)
      ? language
      : AssistantSupportedLanguages.en;
  }

  getFilters(
    app: AppServiceType,
    searchText: string = null,
    filtersPayload: AssistantFilterPayload = null,
    language: string = null
  ): Observable<AssistantFiltersData> {
    const endpoint = `${this.baseUrl}/filters`;
    const params: AssistantFilterParams = {
      language: language ?? this.defaultLanguage,
      limit: 10,
      excludeChat: false,
      useExpertQuery: 0,
      app: app,
    };

    if (!!filtersPayload) {
      params.filter = JSON.stringify(filtersPayload);
    }

    if (!!searchText) {
      params.text = searchText;
    }

    return this.clientService.get(endpoint, params);
  }

  getSuggestion(
    query: string,
    app: AppServiceType,
    language: string = null
  ): Observable<AssistantSuggestData[]> {
    const endpoint = `${this.baseUrl}/suggest`;

    return this.clientService.get(endpoint, {
      query: query,
      maxCount: 10,
      language: language ?? this.defaultLanguage,
      app: app,
    });
  }

  searchDocuments(
    text: string,
    app: AppServiceType,
    filtersPayload: AssistantFilterPayload = null,
    language: string = null,
    limit: number = 9,
    offset: number = 0
  ): Observable<AssistantSearchData> {
    const endpoint = `${this.baseUrl}/search`;
    const params: AssistantSearchParams = {
      text: text,
      limit: limit,
      useExpertQuery: 0,
      excludeChat: false,
      offset: offset,
      language: language ?? this.defaultLanguage,
      disableTracking: false,
      app: app,
    };

    if (!!filtersPayload) {
      params.filter = JSON.stringify(filtersPayload);
    }

    return this.clientService.get(endpoint, params);
  }

  getAssistantFeedbackData(
    search: string = null,
    startDate: string = null,
    endDate: string = null,
    sortOrder: string = null,
    limit: number = 100,
    offset: number = 0
  ): Observable<AssistantFeedbackData> {
    const endpoint = `${this.baseUrl}/documents/feedbacks`;
    const params: AssistantFeedbackDataParams = {
      limit: limit ?? 100,
      offset: offset ?? 0,
      startDate: startDate ?? moment().startOf('day').toISOString(),
      endDate: endDate ?? moment().endOf('day').toISOString(),
    };

    if (search) {
      params.search = search;
    }

    if (sortOrder && sortOrder !== AssistantFeedbackSortOrder.None) {
      params.sortOrder = sortOrder;
      params.sortBy = 'rating';
    }

    return this.clientService.get(endpoint, params);
  }

  getDocumentDetails(
    documentId: string,
    app: AppServiceType,
    language: string = null
  ): Observable<AssistantDocumentDetails> {
    if (documentId) {
      const endpoint = `${this.baseUrl}/documents/${documentId}/details`;

      return this.clientService.get(endpoint, {
        language: language ?? this.defaultLanguage,
        app: app,
      });
    }
  }

  rateDocument(documentId: string, params: AssistantFeedbackParams): Observable<void> {
    const endpoint = `${this.baseUrl}/documents/${documentId}/feedbacks`;

    return this.clientService.post(endpoint, params);
  }

  getDocumentContent(
    documentID: string,
    pdfData = false,
    app: AppServiceType,
    language: string = null
  ): Observable<string> {
    if (documentID) {
      const endpoint = `${this.baseUrl}/documents/${documentID}/download`;
      const responseType = pdfData ? ResponseType.arrayBuffer : ResponseType.text;

      return this.clientService.getDynamicData(endpoint, responseType, {
        language: language ?? this.defaultLanguage,
        app: app,
      });
    }
  }

  getResource(objectId: string, resourceName: string, app: AppServiceType): Observable<Blob> {
    const endpoint = `${this.baseUrl}/resource/environment/project1_p/documents/${objectId}/${resourceName}`;

    return this.clientService.getDynamicData(endpoint, ResponseType.blob, { app });
  }
}
